'use client'

import { useCallback, useMemo, useState } from 'react'

import { LmfAnalyticsProvider, SubmitButton } from '@syconium/little-miss-figgy'

import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useCart } from '../../../../app/_providers/CartProvider.client'
import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useLoop } from '../../../../app/_providers/LoopProvider.client'
import { useOrganization } from '../../../../app/_providers/OrganizationProvider.client'
import { useSession } from '../../../../app/_providers/SessionProvider.client'
import { useSiteWideMessaging } from '../../../../app/_providers/SiteWideMessagingProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { EmbroideryDisclosure } from '../../../../brunswick/components/EmbroideryDisclosure'
import { IntlShippingAndTaxMsg } from '../../../../brunswick/components/IntlShippingAndTaxMsg'
import { CartEmbroideryUpsellProvider } from '../../../../containers/EmbroideryContainer/CartEmbroideryUpsellProvider'
import { getLoopParams } from '../../../../lib/adapters/loop/loopStorage'
import {
  formatPrettyCurrency,
  hasLogoEmbroiderySetup,
  isEmbroideryItem,
} from '../../../../lib/utils'
import { BundleGroup } from '../BundleGroup/BundleGroup'
import { CartEntryItem } from '../CartItem/CartEntryItem/CartEntryItem'
import { LogoDigitizationCartItem } from '../CartItem/EmbroideryGroupItem/LogoDigitizationCartItem'
import { KitGroup } from '../KitGroup/KitGroup'
import {
  CheckoutButtonWrapper,
  EmbroideryWarningContainer,
  EmptyCartMessage,
  EmptyCartMessageWrapper,
  MiniCartMessage,
  TotalSection,
  TotalSectionDivider,
} from '../styles'
import { buildCartIndexWithPending, getEntryKeys } from '../utils'

import { AffirmPromo } from './AffirmPromo/AffirmPromo'
import { useAffirmExperiment } from './AffirmPromo/hooks/useAffirmExperiment'
import { AffirmSdk } from './AffirmPromo/scripts/AffirmSdk'
import { FreeShippingSection } from './FreeShipping/FreeShipping'
import { PriceLine } from './PriceLine'
import {
  CartItemsWrapper,
  CheckoutDisclosureText,
  CheckoutDisclosureWrapper,
  CheckoutErrorMessage,
  IntlMsgWrap,
  PortalDiscountText,
  TotalAndCheckout,
  TotalLineItems,
} from './styles'
import { Upsells } from './Upsells/Upsells'

type MiniCartContentProps = {
  isRevealed: boolean
}

export const MiniCartContent = ({ isRevealed }: MiniCartContentProps) => {
  const {
    cart,
    status: cartStatus,
    errorMessage: cartErrorMessage,
    proceedToCheckout,
    removeItems,
    updateItemsQuantity,
    bundlePrograms,
    pendingLines,
  } = useCart()

  const { showPromo: showAffirmPromo } = useAffirmExperiment({ isRevealed })

  const {
    cart: miniCartCopy,
    magnolia: {
      general: { loading },
    },
  } = useTranslation()
  const { siteWidePrecheckoutMessage: miniCartMessage } = useSiteWideMessaging()
  const { locale, isUSRegion, currency } = useLocalization()
  const { stipend, stipendCad, discountPercentage, b2bStorefrontCollection } = useOrganization()
  const { attributes } = useSession()
  const { inLoopFlow } = useLoop()
  const { loop_total, loop_currency } = getLoopParams()

  const [isCheckoutInFlight, setIsCheckoutInFlight] = useState<boolean>(false)

  const cartItems = Object.values(cart.items)
  // `itemCount` holds the pre-add count while lines load, so check the lines themselves
  const hasLines = cartItems.length > 0 || pendingLines.length > 0

  const {
    logoSetupItem: digitizationLogoSetupItem,
    kitGroups,
    bundleGroups,
    stackedItems,
  } = useMemo(
    () => buildCartIndexWithPending(cart.items, bundlePrograms, pendingLines),
    [cart, bundlePrograms, pendingLines]
  )

  const formattedPrice = formatPrettyCurrency({
    value: cart.totalPrice,
    unit: 'cents',
    currency,
    locale,
  })

  const hasStipendValue = typeof stipend === 'number'
  const hasCanadianStipend = typeof stipendCad === 'number'
  const stipendPriceDisplay = hasCanadianStipend
    ? `${formatPrettyCurrency({
        value: hasStipendValue ? stipend : 0,
        unit: 'dollars',
        currency: 'USD',
        locale: 'en-US',
      })} USD (~${formatPrettyCurrency({
        value: stipendCad,
        unit: 'dollars',
        currency: 'CAD',
        locale: 'en-CA',
      })} CAD)`
    : `${formatPrettyCurrency({
        value: hasStipendValue ? stipend : 0,
        unit: 'dollars',
        currency: 'USD',
        locale: 'en-US',
      })} USD`

  const returnCreditDisplay = inLoopFlow
    ? formatPrettyCurrency({
        value: Number(loop_total),
        unit: 'cents',
        currency: loop_currency ?? 'USD', // Loop returns are domestic only, so `USD` is a valid fallback
        locale,
      })
    : null

  const handleCheckoutClick = useCallback(async () => {
    try {
      setIsCheckoutInFlight(true)
      await proceedToCheckout()
      setIsCheckoutInFlight(false)
    } catch (error) {
      setIsCheckoutInFlight(false)
      reportClientError({
        error,
        context: {
          scope: 'MiniCart',
        },
      })
    }
  }, [proceedToCheckout])

  const showEmbroideryDisclosure = [...cartItems, ...pendingLines].some(item =>
    isEmbroideryItem(item)
  )

  const showCartLogoDigitizationText = Object.values(cart.items).some(item => {
    return hasLogoEmbroiderySetup(item)
  })

  const showB2BDiscountMessage = useMemo(() => {
    if (!discountPercentage) return false

    return cartItems.some(
      item =>
        item.properties['_isPortalColor'] === 'true' || item.properties['_inTeamStore'] === 'true'
    )
  }, [cartItems, discountPercentage])

  const showFreeReturnsDisclosure = useMemo(() => {
    // first-time visitors only
    if (attributes.customerType !== 'firstTime') return false
    // no free returns for `FINAL SALE` items (including embroidery)
    if (Object.values(cart.items).some(e => e.finalSale)) return false
    // respect the checks for the embroidery disclosure
    if (showEmbroideryDisclosure) return false
    // only display for US customers
    return isUSRegion
  }, [attributes.customerType, cart.items, isUSRegion, showEmbroideryDisclosure])

  const hasStipend = b2bStorefrontCollection && stipend !== null

  return (
    <CartEmbroideryUpsellProvider>
      {showAffirmPromo && <AffirmSdk />}
      <LmfAnalyticsProvider title='Cart Content'>
        {!inLoopFlow ? <FreeShippingSection cartTotal={cart.totalPrice} /> : null}
        {!hasLines ? (
          <EmptyCartMessageWrapper>
            <EmptyCartMessage>{miniCartCopy.emptyCartMessage}</EmptyCartMessage>
          </EmptyCartMessageWrapper>
        ) : null}
        {hasLines ? (
          <CartItemsWrapper>
            {digitizationLogoSetupItem && (
              <LogoDigitizationCartItem cartItem={digitizationLogoSetupItem} />
            )}
            {kitGroups.map(group => {
              const entryKeys = getEntryKeys(group)
              const key = entryKeys.join('')
              return (
                <KitGroup
                  key={key}
                  entries={group}
                  eventCategory='minicart'
                  embroideryLabel={miniCartCopy.embroideryItemLabel}
                  decrementQuantity={() => {
                    updateItemsQuantity(entryKeys, -1)
                  }}
                  incrementQuantity={() => {
                    updateItemsQuantity(entryKeys, 1)
                  }}
                  setQuantity={(newQuantity: number) => {
                    updateItemsQuantity(entryKeys, () => newQuantity)
                  }}
                  removeAll={() => {
                    removeItems(entryKeys)
                  }}
                  removeItem={(itemKey: string) => removeItems([itemKey])}
                />
              )
            })}
            {bundleGroups.map((group, index) => (
              <BundleGroup
                key={`${group.minQty}-for-${group.discountValue}-${index}`}
                bundleGroup={group}
                bundlePrograms={bundlePrograms}
                eventCategory='minicart'
              />
            ))}
            {stackedItems.map(entry => (
              <CartEntryItem key={entry.item.key} entry={entry} eventCategory='minicart' />
            ))}
          </CartItemsWrapper>
        ) : null}
        <TotalAndCheckout>
          {hasLines ? <TotalSectionDivider /> : null}
          <TotalSection>
            <EmbroideryWarningContainer>
              {showEmbroideryDisclosure ? (
                <EmbroideryDisclosure
                  style={{ marginBottom: '20px' }}
                  showCartLogoDigitizationText={showCartLogoDigitizationText}
                />
              ) : null}
            </EmbroideryWarningContainer>
            {miniCartMessage && <MiniCartMessage>{miniCartMessage}</MiniCartMessage>}
            <TotalLineItems>
              {hasStipend && (
                <PriceLine
                  type='stipend'
                  label={miniCartCopy.stipendBalanceLabel}
                  price={stipendPriceDisplay}
                />
              )}
              <PriceLine
                type='subtotal'
                label={miniCartCopy.subtotalLabel}
                price={formattedPrice}
                loading={cartStatus === 'pending'}
              />
              {showAffirmPromo && (
                <LmfAnalyticsProvider title='Affirm BNPL'>
                  <AffirmPromo />
                </LmfAnalyticsProvider>
              )}

              {returnCreditDisplay && (
                <PriceLine
                  type='credit'
                  label={miniCartCopy.creditLabel}
                  price={returnCreditDisplay}
                />
              )}
              {showB2BDiscountMessage ? (
                <PortalDiscountText>
                  {b2bStorefrontCollection?.name} {discountPercentage}
                  {miniCartCopy.portalDiscountMessage}
                </PortalDiscountText>
              ) : null}
            </TotalLineItems>
            {!isUSRegion ? (
              <IntlMsgWrap>
                <IntlShippingAndTaxMsg />
              </IntlMsgWrap>
            ) : null}
          </TotalSection>

          <CheckoutButtonWrapper>
            <SubmitButton
              allCaps
              block
              size='large'
              data-testid='checkout-button'
              borderless={cartItems.length === 0 || isCheckoutInFlight}
              disabled={cartItems.length === 0 || isCheckoutInFlight}
              onClick={handleCheckoutClick}
              status={isCheckoutInFlight ? 'pending' : 'idle'}
              pendingLabel={loading}
              resolvedLabel={miniCartCopy.proceedToCheckoutCTA}
              idleChildren={miniCartCopy.proceedToCheckoutCTA}
            />
            {cartErrorMessage ? (
              <CheckoutErrorMessage>{cartErrorMessage}</CheckoutErrorMessage>
            ) : null}
          </CheckoutButtonWrapper>
          {showFreeReturnsDisclosure && (
            <CheckoutDisclosureWrapper>
              <CheckoutDisclosureText>
                {miniCartCopy.returns.freeReturnsAndExchanges}
              </CheckoutDisclosureText>
            </CheckoutDisclosureWrapper>
          )}
        </TotalAndCheckout>
        {isRevealed && <Upsells />}
      </LmfAnalyticsProvider>
    </CartEmbroideryUpsellProvider>
  )
}
